import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import {
  ordersResponseSchema,
  postOrderResponseSchema,
  showOrderResponseSchema,
  updateOrderResponseSchema,
} from '@rd/schemas/order.schema';
import {
  FeatureFlags,
  HttpError,
  Notification,
  Order,
  OrderIndexParams,
  OrderIndexParamsStatus,
  OrderItem,
  OrderItemBase,
  OrderItemStatus,
} from '@rd/types';
import { NotificationModel } from '@rd/types/notification.model';
import { NydusRecipient } from '@rd/types/nydus-recipient.type';
import { OrderItemModel } from '@rd/types/order-item.model';
import { OrderModel } from '@rd/types/order.model';
import {
  OrdersResponse,
  PostOrderResponse,
  ShowOrderResponse,
} from '@rd/types/order.response';
import { Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  scan,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

// This is the NN representation of an Address. RD Address has `addressLine1` and `addressLine2`.
// TODO: Either change the RD `Address` from `addressLine1` to `line_1`, or NN to change. Otherwise, we'll end up having 2
// ways of representing addresses, and we need to write a lot of adapter code.

export interface CreateOrderParams {
  order: {
    recipient: NydusRecipient;
  };
}

export interface UpdateOrderParams extends CreateOrderParams {
  id: string;
}

interface UpdateOrderResponse extends Omit<PostOrderResponse, 'included'> {}

@Injectable({
  providedIn: 'root',
})
export class OrderApiService {
  private nydusNetworkApiService = inject(NydusNetworkApiDataService);
  private apiValidationService = inject(ApiValidationService);
  private featureFlagService = inject(FeatureFlagService);

  getOrders(orderIndexParams: Partial<OrderIndexParams>): Observable<Order[]> {
    const orderParams = this.buildParams(orderIndexParams);

    return this.nydusNetworkApiService
      .get<OrdersResponse>('orders', orderParams)
      .pipe(
        tap((response: OrdersResponse) => {
          this.apiValidationService.validate<OrdersResponse>({
            apiName: `orders?${decodeURIComponent(orderParams.toString())}`,
            schema: ordersResponseSchema,
            response,
          });
        }),
        map((response: OrdersResponse) => {
          return this.transformGetOrdersResponse(response);
        }),
      );
  }

  createOrder(params?: CreateOrderParams): Observable<Order> {
    const order = params?.order;
    const shoppingCartEnabled = this.featureFlagService.isEnable(
      FeatureFlags.ShoppingCart,
    );

    return this.nydusNetworkApiService
      .post<PostOrderResponse>(`orders`, {
        order: { ...order, disableShoppingCart: !shoppingCartEnabled },
      })
      .pipe(
        tap((createOrderResponse) => {
          this.apiValidationService.validate<PostOrderResponse>({
            apiName: '(POST) orders',
            schema: postOrderResponseSchema,
            response: createOrderResponse,
          });
        }),
        map((createOrderResponse) => {
          return this.transformCreateOrderResponse(createOrderResponse);
        }),
      );
  }

  update(params: UpdateOrderParams): Observable<Order> {
    const { id, order } = params;

    // PostOrderResponse should be the same as PutOrderResponse, and should by right use the same transformation logic.
    // Except for `included`. This is why we app
    return this.nydusNetworkApiService
      .put<PostOrderResponse>(`orders/${id}`, { order })
      .pipe(
        tap((updateOrderResponse) => {
          this.apiValidationService.validate<PostOrderResponse>({
            apiName: `(PUT) orders/${id}`,
            schema: updateOrderResponseSchema,
            response: updateOrderResponse,
          });
        }),
        map((updateOrderResponse: UpdateOrderResponse) => {
          // included: - currently, the NN API doesn't have included in `PutOrderResponse` - it will be added in future.
          return this.transformCreateOrderResponse({
            ...updateOrderResponse,
            included: [],
          });
        }),
      );
  }

  buildParams(orderIndexParams: Partial<OrderIndexParams>): HttpParams {
    let params = new HttpParams();

    if (!orderIndexParams) {
      return params;
    }

    // Destructure, but set default arguments of no filter for both status and orderItemType
    const {
      filter: { status, orderItemType } = { status: [], orderItemType: [] },
      sort,
    } = orderIndexParams;

    if (sort) {
      params = params.set('sort', sort);
    }

    if (status.length > 0) {
      // For "completed" status, should match to "fulfilled"
      // For "processing" status, should also include other statuses which fall under "pending", but do not
      // have the word "processing" as it's status i.e. "fulfilling" IS still processing.
      const statusMapping = {
        completed: 'fulfilled',
        processing: 'processing,fulfilling',
        cancelled: 'cancelled',
        failed: 'failed',
      };

      // Construct a string of
      // 'fulfilled,processing,fulfilling,failed' if all filters were checked
      const orderItemStatus = Object.keys(statusMapping).reduce(
        // @ts-ignore
        (accumulator = null, current: OrderIndexParamsStatus) => {
          if (status.includes(current)) {
            // @ts-ignore
            accumulator = [accumulator, statusMapping[current]]
              .filter((_status) => _status != null)
              .join(',');
          }

          return accumulator;
        },
        null,
      );

      params = params.set('filter[order_item.status][$eq]', orderItemStatus);
    }

    if (orderItemType.length > 0) {
      // Convert giftCard, pointsTransfer, cashRedemption to
      // GiftCardOrderItem/PhysicalGiftCardOrderItem, PointsTransferOrderItem, CashRedemptionOrderItem
      // Digital GCs and Physical GCs will both be shown when the GC checkbox is ticked
      const prepareOrderItemTypeParams = (orderItemTypeStr: string) => {
        const capitalizedFirstLetter = orderItemTypeStr[0].toUpperCase();
        const restOfTheLetters = orderItemTypeStr.slice(1);

        if (orderItemTypeStr === 'giftCard') {
          return `GiftCardOrderItem,PhysicalGiftCardOrderItem`;
        } else {
          return `${capitalizedFirstLetter}${restOfTheLetters}OrderItem`;
        }
      };

      params = params.set(
        'filter[order_item.type][$eq]',
        orderItemType
          .map((orderItem) => {
            return prepareOrderItemTypeParams(orderItem);
          })
          .join(','),
      );
    }

    return params;
  }

  private transformCreateOrderResponse(
    orderResponse: PostOrderResponse,
  ): Order {
    const { data, included } = orderResponse;
    return this.transformOrderModel(data, included);
  }

  confirmOrder(orderId?: string): Observable<Order> {
    return this.nydusNetworkApiService
      .post<PostOrderResponse>(`orders/${orderId}/checkout`, {})
      .pipe(
        map((createOrderResponse) => {
          return this.transformCreateOrderResponse({
            ...createOrderResponse,
            included: [],
          });
        }),
      );
  }

  pollOrder(
    id: string,
    interval: number = 5000,
    maxAttempts: number = 5,
  ): Observable<Order | HttpError> {
    return timer(0, interval)
      .pipe(
        switchMap(() => {
          return this.getOrder(id).pipe(
            // Make sure to continue polling on NN API errors
            catchError((error) => of(error)),
          );
        }),
        scan(this.accumulateResults, []),
        exhaustMap((result) => this.checkAttempts(result, maxAttempts)),
        filter((result) => this.acceptedOrder(result)),
        take(1),
      )
      .pipe(
        catchError((err) => {
          if (this.isResultOrder(err)) {
            return of(err);
          } else {
            return throwError(err);
          }
        }),
      );
  }

  getOrder(id: string): Observable<Order> {
    return this.nydusNetworkApiService
      .get<ShowOrderResponse>(`orders/${id}`)
      .pipe(
        tap((orderResponse) => {
          this.apiValidationService.validate<ShowOrderResponse>({
            apiName: `orders/${id}`,
            schema: showOrderResponseSchema,
            response: orderResponse,
          });
        }),
        map((orderResponse) => {
          return this.transformShowOrderResponse(orderResponse);
        }),
      );
  }

  cancelOrderItem(id: string): Observable<any> {
    return this.nydusNetworkApiService.post<void>(
      `order_items/${id}/cancellations`,
      {},
      undefined,
      {
        /**
         * NOTE(@SonTruongAscenda):
         *
         * We need to send the `idempotency-key` in the header to make this API idempotent.
         * In the future, we might use the `idempotency-key` for all API requests as well.
         *
         * @see https://github.com/Kaligo/nydus_network/pull/6855
         */
        'idempotency-key': `order-item-${id}`,
      },
    );
  }

  private transformShowOrderResponse(
    showOrderResponse: ShowOrderResponse,
  ): Order {
    const { data, included } = showOrderResponse;
    return this.transformOrderModel(data, included);
  }

  private transformOrderModel(
    orderModel: OrderModel,
    orderItemModel: (OrderItemModel | NotificationModel)[],
  ): Order {
    const { id, attributes, type } = orderModel;
    const orderItems: OrderItem[] = [];
    const notifications = orderItemModel
      .filter(
        (notification: OrderItemModel | NotificationModel) =>
          notification.type === 'notification',
      )
      .map((notification) => notification as NotificationModel);

    for (const orderItem of orderItemModel) {
      if (orderItem.type !== 'notification') {
        orderItems.push({
          id: orderItem.id,
          type: orderItem.type,
          // @ts-ignore
          attributes: {
            ...orderItem.attributes,
          },
          notifications: notifications
            .filter(
              (notification) =>
                notification.relationships.notificationTopic.data.id ===
                orderItem.id,
            )
            .map((notification) =>
              this.transformNotificationModel(notification),
            ),
        });
      }
    }

    const {
      userId,
      status,
      stripePaymentIntentClientSecret,
      cashPaid,
      pointsPaid,
      pointsAmount,
      cashAmount,
      fulfillmentStatus,
      data,
      createdAt,
      updatedAt,
    } = attributes;

    return {
      id,
      type,
      userId,
      status,
      stripePaymentIntentClientSecret,
      pointsPaid,
      cashPaid,
      pointsAmount,
      cashAmount,
      orderItems,
      fulfillmentStatus,
      data,
      createdAt,
      updatedAt,
    };
  }

  private checkAttempts(
    result: (Order | HttpError)[],
    maxAttempts: number,
  ): Observable<Order | HttpError> {
    // reverse so that Array.find returns us the latest element
    const lastOrder = [...result]
      .reverse()
      .find((item) => this.isResultOrder(item));
    let res: Order | HttpError;

    // @ts-ignore
    if (this.isResultOrder(lastOrder)) {
      res = lastOrder;
    } else {
      // just return the last item if its an error
      res = result[result.length - 1];
    }

    if (result.length >= maxAttempts) {
      // terminate timer loop by throwing error
      return throwError(res);
    } else {
      return of(res);
    }
  }

  private isResultOrder(result: Order | HttpError): result is Order {
    if (result && (result as Order).id) {
      return true;
    } else {
      return false;
    }
  }

  private acceptedOrder(result: Order | HttpError): boolean {
    return (
      this.isResultOrder(result) &&
      [OrderItemStatus.Fulfilled, OrderItemStatus.Failed].includes(
        result.fulfillmentStatus,
      )
    );
  }

  private accumulateResults(
    all: (Order | HttpError)[],
    curr: Order | HttpError,
  ): (Order | HttpError)[] {
    return [...all, curr];
  }

  private transformGetOrdersResponse(response: OrdersResponse): Order[] {
    // OrdersResponse contains:
    // response.data - list of orders
    // response.included - combination of both OrderItemModel and NotificationModel
    // What has to happen is:
    // Iterate through all the orders (1)
    // Iterate through all the order items that are contained in the order - must also not be notifications (2)
    // Iterate through all the notifications and attach them via `.push` to the order items they are related to (3)
    // Orders in (2) would then be attached via `.push` to each order in (1) (4)

    return response.data.map((order: OrderModel) => {
      const { attributes } = order;
      let orderItems: OrderItemBase[] = [];

      // TODO: This is to handle the future setup where orderItems is an array
      // Can copy the logic in the else statement when orderItems is an array to have forward/backward compatibility
      if (Array.isArray(order.relationships.orderItem)) {
        orderItems = order.relationships.orderItem.map((item) => ({
          id: item.data.id,
          type: item.data.type,
          notifications: [],
        }));
      } else {
        // (2)
        const includedOrderItems = response.included.filter(
          (orderItem: OrderItemModel | NotificationModel) => {
            return (
              orderItem.type !== 'notification' &&
              orderItem.relationships.order.data.id === order.id
            );
          },
        );

        // (3)
        const notifications = response.included
          .filter((notification: OrderItemModel | NotificationModel) => {
            return notification.type === 'notification';
          })
          .map((notification) => notification as NotificationModel);

        includedOrderItems.map((includedOrderItem) => {
          const relevantNotifications = notifications
            .filter(
              ({
                relationships: {
                  notificationTopic: {
                    data: { id },
                  },
                },
              }) => {
                return id === includedOrderItem.id;
              },
            )
            .map((notification: NotificationModel) =>
              this.transformNotificationModel(notification),
            );

          // (4)
          orderItems.push({
            id: includedOrderItem.id,
            type: (includedOrderItem as OrderItemModel).type,
            attributes: (includedOrderItem as OrderItemModel).attributes,
            notifications: relevantNotifications,
          });
        });
      }
      return {
        id: order.id,
        type: order.type,
        ...attributes,
        orderItems,
      } as Order;
    });
  }

  private transformNotificationModel(
    notificationModel: NotificationModel,
  ): Notification {
    const {
      id,
      attributes: { channel, eventName },
    } = notificationModel;

    return {
      id,
      type: 'notification',
      channel,
      eventName,
    };
  }
}
